import { T, useGT } from 'gt-react';
import {
  ArrowClockwiseIcon,
  SpinnerGapIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { Button } from '@/components/ui/button';
import { Can } from '@/hooks/useCan';
import { usePreviewValidationRule } from '@/api/rpc/hooks/inventory-management/useValidationRules';
import { EvaluationResultView } from './EvaluationResultView';

/**
 * A read-only evaluation of the saved rule, on request. Nothing here is
 * recorded: it answers "what would this rule flag right now" without writing a
 * run, touching the rule's violations, or staging a review — which is why it is
 * a button rather than something the page does on load.
 */
export function ValidationRulePreviewPanel({ ruleId }: { ruleId: string }) {
  const gt = useGT();
  const preview = usePreviewValidationRule();

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium">
          <T context="heading of the on-demand preview section on a validation rule's page">
            Preview
          </T>
        </h2>
        <Can I={Action.Update} a={Subject.Product}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={preview.isPending}
            onClick={() => preview.mutate({ ruleId })}
          >
            {preview.isPending ? (
              <SpinnerGapIcon className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowClockwiseIcon className="h-4 w-4" />
            )}
            {preview.data ? (
              <T context="button that previews a saved validation rule again">
                Preview again
              </T>
            ) : (
              <T context="button that previews a saved validation rule">
                Run preview
              </T>
            )}
          </Button>
        </Can>
      </div>

      <p className="text-sm text-muted-foreground">
        <T context="explains what the preview on a validation rule's page does and does not do">
          A capped, read-only evaluation of the rule as it stands right now. It
          reports the first few disagreeing groups with a sample of their rows —
          use Run now for the whole picture. Nothing is saved and no review is
          staged.
        </T>
      </p>

      {preview.error && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <WarningCircleIcon
            className="mt-0.5 h-4 w-4 shrink-0"
            weight="fill"
          />
          {/* The server's own rejection, verbatim: it names the rule's fields. */}
          <span>{preview.error.message}</span>
        </div>
      )}

      {preview.isPending && !preview.data && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <SpinnerGapIcon className="h-4 w-4 animate-spin" />
          <T context="loading state while a validation rule preview runs">
            Evaluating the rule…
          </T>
        </p>
      )}

      {preview.data && (
        <EvaluationResultView
          {...preview.data}
          emptyMessage={gt('Nothing disagrees in what the preview looked at.', {
            context:
              'empty state after previewing a saved validation rule and finding no disagreements',
          })}
        />
      )}
    </section>
  );
}
